"use client";

import React, { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  num: "01",
  title: "Doctor Approval",
  desc: "Your prescription is digitally verified by our network of Atlanta's top medical professionals, ensuring clinical accuracy and patient safety from the very start."
}, {
  num: "02",
  title: "Pharmacy Sync",
  desc: "We coordinate directly with local pharmacies to prepare your medication. Our real-time integration ensures your order is prioritized for immediate fulfillment."
}, {
  num: "03",
  title: "Secure Packaging",
  desc: "Medications are sealed in tamper-evident, temperature-controlled packaging. We maintain the highest standards of confidentiality and medical compliance."
}, {
  num: "04",
  title: "Fast Transit",
  desc: "Our professional couriers navigate Atlanta's routes with precision. Real-time GPS tracking allows you to monitor your delivery's progress every step of the way."
}, {
  num: "05",
  title: "Doorstep Verification",
  desc: "Upon arrival, we perform a secure hand-off with identity verification, completing the care cycle with the convenience and reliability you deserve."
}];
function TimelineItem({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.4,
    once: false
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="223698" ref={ref} data-index={index} className="relative flex flex-col md:flex-row items-center justify-between w-full mb-24 md:mb-40 last:mb-0 group">
      {}
      <div className="absolute left-[15px] md:left-1/2 w-4 h-4 rounded-full bg-background border-2 transform -translate-x-1/2 z-20 transition-colors duration-500 ease-in-out" style={{
      borderColor: isVisible ? "var(--primary)" : "var(--border)",
      backgroundColor: isVisible ? "var(--primary)" : "var(--background)",
      boxShadow: isVisible ? "0 0 15px var(--primary)" : "none"
    }} />

      {}
      <div className={`w-full md:w-[45%] pl-16 md:pl-0 ${isEven ? "md:text-right md:pr-16" : "md:order-2 md:text-left md:pl-16"}`}>
        <div className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className={`text-7xl md:text-9xl font-bold font-mono leading-none mb-6 transition-all duration-1000 ease-out origin-center ${isVisible ? "scale-100 text-primary/10" : "scale-50 text-muted/5"}`}>
            {step.num}
          </div>

          {}
          <div className={`transition-opacity duration-1000 delay-300 ${isVisible ? "opacity-100" : "opacity-0"}`}>
            <h3 className="text-2xl md:text-4xl font-bold font-default mb-4 tracking-tight text-foreground">
              {step.title}
            </h3>
            <p className="text-muted-foreground text-lg md:text-xl leading-relaxed font-default">
              {step.desc}
            </p>
          </div>
        </div>
      </div>

      {}
      <div className={`hidden md:block w-[45%] ${isEven ? "md:order-2" : ""}`} />
    </div>;
}
export default function TheCollaborativeProcess() {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [fillHeight, setFillHeight] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const startOffset = windowHeight / 2;
      const scrolledPastStart = startOffset - rect.top;
      if (scrolledPastStart < 0) {
        setFillHeight(0);
      } else {
        const progress = scrolledPastStart / rect.height * 100;
        setFillHeight(Math.min(Math.max(progress, 0), 100));
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="from-script-to-step" className="bg-background text-foreground py-24 md:py-48 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {}
        <div className="text-center mb-32 md:mb-48 relative z-10">
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold font-default tracking-tighter mb-8 uppercase">
            From <span className="text-primary">Script</span> to<br />
            Step
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto font-default font-light">
            A clinical-grade logistics framework designed to bridge the gap between your doctor's office and your front door.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto" ref={timelineRef}>
          
          {}
          <div className="absolute left-[15px] md:left-1/2 top-0 bottom-0 w-[2px] bg-border transform -translate-x-1/2 z-0" />
          
          {}
          <div className="absolute left-[15px] md:left-1/2 top-0 w-[2px] bg-gradient-to-b from-primary to-secondary transform -translate-x-1/2 z-10 transition-all duration-75 ease-linear shadow-[0_0_15px_var(--primary)]" style={{
          height: `${fillHeight}%`
        }} />

          {}
          <div className="relative z-20 pt-10 pb-10">
            {steps.map((step, index) => <TimelineItem key={step.num} step={step} index={index} />)}
          </div>
          
        </div>
      </div>
    </section>;
}